import { useHandler } from "@redotech/react-util/hook";
import { useTranslation } from "@redotech/redo-i18n";
import SwitchHorizontalIcon from "@redotech/redo-icons/switch-horizontal-01_filled.svg";
import { RedoCommandMenuItem } from "@redotech/redo-web/arbiter-components/command-menu/redo-command-menu";
import { useMemo } from "react";

export interface FlippableBranches {
  nextTrueId?: string | undefined;
  nextFalseId?: string | undefined;
}

/**
 * "Flip branches" swaps a condition's Yes and No children, carrying each
 * child's whole subtree with it and leaving the condition itself untouched.
 */
export function useFlipBranchesMenuItems<Step extends FlippableBranches>({
  id,
  step,
  readonly,
  updateStep,
}: {
  id: string;
  step: Step;
  readonly: boolean;
  /**
   * The intersection is what a spread of a generic produces; callers may take
   * a wider step type than `Step`.
   */
  updateStep: (id: string, updatedStep: Step & FlippableBranches) => void;
}): RedoCommandMenuItem[] {
  const { t } = useTranslation();
  const handleFlipBranches = useHandler(() => {
    updateStep(id, {
      ...step,
      nextTrueId: step.nextFalseId,
      nextFalseId: step.nextTrueId,
    });
  });

  return useMemo(() => {
    if (readonly || !step.nextTrueId || !step.nextFalseId) {
      return [];
    }

    return [
      {
        text: t("Flip branches"),
        onClick: handleFlipBranches,
        Icon: SwitchHorizontalIcon,
        theme: "normal" as const,
      },
    ];
  }, [readonly, step.nextTrueId, step.nextFalseId, handleFlipBranches, t]);
}
